import React, { useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { coreRiskLookupError } from '../utils/extendFinderMessages';
import {
  inferExtendableVehicleClass,
  isExtendableForExtension,
  mergeExtendFinderPolicyRow,
  resolveExtensionVehicleClass
} from '../utils/extendPolicyInference';

const ALLOWED_EXTENSION_CLASSES = new Set([
  'Motor Private',
  'Motor Commercial',
  'Motor Commercial Institutional',
  'Motor Commercial General Cartage',
  'Tuk Tuk PSV',
  'Tuktuk PSV',
  'PSV Chauffer Driven'
]);

function normalizeReg(v) {
  return String(v || '').trim().toUpperCase().replace(/\s/g, '');
}

function normalizePolicyNo(p) {
  return String(p?.policyNo || p?.policy_number || '')
    .trim()
    .replace(/\.+$/, '')
    .toUpperCase();
}

function displayPolicyNo(p) {
  return String(p?.policyNo || p?.policy_number || '')
    .trim()
    .replace(/\.+$/, '');
}

/** One row per policy number; keep the Core row with the latest polBatchNo when present. */
function dedupePoliciesByPolicyNo(rows) {
  const byNo = new Map();
  for (const p of rows) {
    const key = normalizePolicyNo(p);
    if (!key) continue;
    const prev = byNo.get(key);
    if (!prev || Number(p.polBatchNo || 0) > Number(prev.polBatchNo || 0)) {
      byNo.set(key, p);
    }
  }
  return [...byNo.values()];
}

export default function ExtendPolicyFinder({ onProceed }) {
  const [reg, setReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const [error, setError] = useState('');
  const [policies, setPolicies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const coreAbortRef = useRef(null);

  const normalized = useMemo(() => normalizeReg(reg), [reg]);
  const selectedPolicy = useMemo(
    () => policies.find((p) => String(p.policyNo || p.policy_number || p.id) === String(selectedId)) || null,
    [policies, selectedId]
  );

  const handleSearch = async () => {
    if (!normalized) {
      setError('Enter a vehicle registration');
      return;
    }
    if (coreAbortRef.current) coreAbortRef.current.abort();
    const coreController = new AbortController();
    coreAbortRef.current = coreController;

    setLoading(true);
    setError('');
    setPolicies([]);
    setSelectedId(null);
    try {
      const corePromise = fetch(API_ENDPOINTS.corePoliciesByRisk(normalized), {
        method: 'GET',
        signal: coreController.signal
      });
      const portalRes = await fetch(API_ENDPOINTS.extensionPortalPoliciesByRegistration(normalized), {
        method: 'GET'
      });

      const portalJson = await portalRes.json().catch(() => ({}));
      if (!portalRes.ok || portalJson.success === false) {
        coreController.abort();
        throw new Error(portalJson.error || portalJson.message || `Portal lookup failed (${portalRes.status})`);
      }

      const portalPolicies = portalJson.portalPolicies || [];
      const portalSet = new Set(
        (portalJson.policyNumbers || [])
          .map((pn) => String(pn || '').trim().toUpperCase())
          .filter(Boolean)
      );

      if (portalSet.size === 0) {
        coreController.abort();
        setError(
          'No cover for this registration was issued through Agent New Portal. Extension is only available for policies created in this app.'
        );
        return;
      }

      const coreRes = await corePromise;
      const coreData = await coreRes.json().catch(() => null);
      if (!coreRes.ok) {
        throw new Error(coreRiskLookupError(coreRes.status, coreData) || 'Failed to fetch policies from core');
      }

      const coreList = dedupePoliciesByPolicyNo(
        Array.isArray(coreData) ? coreData : coreData ? [coreData] : []
      );
      const coreByPolicy = new Map(
        coreList
          .filter((p) => portalSet.has(normalizePolicyNo(p)))
          .map((p) => [normalizePolicyNo(p), p])
      );

      const allowedList = portalPolicies
        .map((portal) => mergeExtendFinderPolicyRow(portal, coreByPolicy.get(normalizePolicyNo(portal)) || null))
        .filter((p) => isExtendableForExtension(p, p.portalVehicleClass, ALLOWED_EXTENSION_CLASSES));
      if (allowedList.length === 0) {
        setError('No extendable policy found among your portal-issued covers. Only allowed classes are supported.');
        return;
      }
      setPolicies(allowedList);
      if (allowedList.length === 1) {
        setSelectedId(String(allowedList[0]?.policyNo || allowedList[0]?.policy_number || allowedList[0]?.id));
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setError(e.message || 'Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    if (!selectedPolicy) return;
    const policyNoForSeed = displayPolicyNo(selectedPolicy);
    const policyForProceed = policyNoForSeed
      ? { ...selectedPolicy, policyNo: policyNoForSeed, policy_number: policyNoForSeed }
      : selectedPolicy;
    const portalVehicleClass = policyForProceed.portalVehicleClass || null;
    const coreInferred = inferExtendableVehicleClass(policyForProceed);
    if (!isExtendableForExtension(policyForProceed, portalVehicleClass, ALLOWED_EXTENSION_CLASSES)) {
      setError('Selected policy class is not eligible for extension.');
      return;
    }
    const inferredClass = resolveExtensionVehicleClass(
      portalVehicleClass,
      coreInferred,
      ALLOWED_EXTENSION_CLASSES
    );
    setProceeding(true);
    setError('');
    try {
      // Do not hardcode tomorrow — parent seeds via getTpoExtendEarliestStartYmd (today when expired).
      await onProceed({
        registrationNumber: normalized,
        inferredClass,
        selectedPolicy: policyForProceed
      });
    } catch (e) {
      setError(e?.message || 'Unable to continue with this policy. Please try another registration or contact support.');
    } finally {
      setProceeding(false);
    }
  };

  return (
    <div className="extend-finder">
      <div className="extend-finder__card">
        <h2 className="extend-finder__title">Find existing policy</h2>
        <p className="extend-finder__subtitle">
          Search by vehicle registration. Portal-issued covers are matched to a live Core risk lookup.
        </p>

        <div className="extend-finder__row">
          <input
            type="text"
            value={reg}
            onChange={(e) => setReg(e.target.value)}
            placeholder="e.g. KDD321L"
            disabled={loading}
            className="extend-finder__input"
          />
          <button type="button" className="extend-finder__btn" onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {error && <div className="extend-finder__error">{error}</div>}

        {policies.length > 0 && (
          <div className="extend-finder__results">
            <div className="extend-finder__resultsHeader">Your portal policies (this registration)</div>
            <div className="extend-finder__list">
              {policies.map((p, idx) => {
                const id = displayPolicyNo(p) || String(p.id || '');
                const active = id === String(selectedId);
                const insured = String(p.portalInsuredName || p.clientName || p.client_name || '').trim();
                const product = String(
                  p.displayProduct || p.portalVehicleClass || p.product || p.productDesc || ''
                ).trim();
                const certificateNo = String(p.portalCertificateNo || '').trim();
                const policyBalance = Number(p.policyBalance ?? 0);
                const rowKey = `${id}-${p.polBatchNo != null ? p.polBatchNo : idx}`;
                return (
                  <button
                    key={rowKey}
                    type="button"
                    onClick={() => setSelectedId(id)}
                    className={`extend-finder__item ${active ? 'active' : ''}`}
                  >
                    <div className="extend-finder__itemTop">
                      <strong>{id}</strong>
                    </div>
                    {insured && <div className="extend-finder__meta">{insured}</div>}
                    {product && <div className="extend-finder__meta">{product}</div>}
                    {certificateNo && (
                      <div className="extend-finder__meta">Certificate {certificateNo}</div>
                    )}
                    {Number.isFinite(policyBalance) && policyBalance > 0 && (
                      <div className="extend-finder__meta" style={{ color: '#8a5b00', fontWeight: 600 }}>
                        Outstanding Balance: KES {Math.round(policyBalance).toLocaleString()}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="extend-finder__btn extend-finder__continue"
              onClick={handleProceed}
              disabled={!selectedPolicy || proceeding}
            >
              {proceeding ? 'Loading policy details...' : 'Continue to extension'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
